import React, { useEffect } from 'react';
import BorderedSection from '../../../shared/borderedSection';
import {
  WdTab,
  WdTabs,
} from '../../../devices/residential-devices/thermostat/components/schedule/weekday-tabs';
import {
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  List,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { ScheduleItem } from '../../../devices/residential-devices/thermostat/components/schedule/schedule-item';
import {
  dailyScheduleDefault,
  DayOfWeek,
  dayOfWeekLabels,
  dayOfWeekLabelsMobile,
  EmptyWeeklySchedule,
  eventLabels,
  MyScheduleSetting,
  TimeOfDay,
} from '../../../devices/residential-devices/thermostat/types';
import {
  EnumScheduleType,
  ThermostatPendingSchedule,
  ThermostatScheduleTemplateValue,
} from '../../../../../types/generated-types';
import _ from 'lodash';
import { daysToDisplay } from '../../../devices/residential-devices/thermostat/components/helpers/days-to-display';
import {
  Fahrenheit,
  TemperatureUnit,
} from '../../../../system/models/temperatureUnits';
import { useAuthenticator } from '../../../../auth/AuthenticationContext';
import { useIsSmallScreen } from '../../../../system/SystemMediaQueriesProvider';
import { SetpointLimitSettings } from '../../types/setpointLimitSettings';
import { defaultAbsoluteLimits } from '../../../helpers';

interface ScheduleDetailEditorProps {
  scheduleValues: ThermostatScheduleTemplateValue | ThermostatPendingSchedule;
  setScheduleValues: (
    schedule: ThermostatScheduleTemplateValue | ThermostatPendingSchedule,
  ) => void;
  setScheduleType: (newScheduleType: EnumScheduleType) => void;
  setpointLimitSettings?: SetpointLimitSettings;
  scheduleType: EnumScheduleType;
}

const label: string[] = ['Weekday', 'Weekend'];

export function ScheduleDetailEditor(props: ScheduleDetailEditorProps) {
  const {
    scheduleValues,
    setScheduleValues,
    setScheduleType,
    setpointLimitSettings,
    scheduleType,
  } = props;
  const [dailySchedule, setDailySchedule] = React.useState();

  const [selectedDay, setSelectedDay] = React.useState(
    scheduleType === EnumScheduleType.WeekdayWeekend
      ? DayOfWeek.MO
      : DayOfWeek.SU,
  );

  const isSmall = useIsSmallScreen();

  /**
   * USE EFFECT
   */

  useEffect(() => {
    if (scheduleValues?.value) {
      setDailySchedule(scheduleValues.value[selectedDay]);
    }
  }, [scheduleValues, selectedDay]);

  /**
   * HANDLERS
   */

  const handleDaySelect = (
    _event: React.SyntheticEvent,
    newValue: DayOfWeek,
  ) => {
    setSelectedDay(newValue);
  };

  const handleTypeSelect = (type: EnumScheduleType) => {
    handleScheduleTypeChange(type);
    // If the new schedule type is weekday-weekend, select Monday (the weekday) by default, otherwise select Sunday
    if (type === EnumScheduleType.WeekdayWeekend) {
      setSelectedDay(DayOfWeek.MO);
    } else {
      setSelectedDay(DayOfWeek.SU);
    }
  };

  const handleScheduleChange = (
    operation: 'edit_setpoints' | 'edit_time',
    dayOfWeek: DayOfWeek,
    timeOfDay: TimeOfDay,
    newTimeOfDay?: TimeOfDay,
    scheduleSetting?: MyScheduleSetting,
  ) => {
    // Create a copy of the current schedule object, or a new schedule object if none exists
    const _newSchedule = { ...scheduleValues };
    if (scheduleValues && scheduleValues.value) {
      _newSchedule.value = _.cloneDeep(scheduleValues.value);
    } else {
      _newSchedule.value = _.cloneDeep(EmptyWeeklySchedule);
    }

    // Perform the specified update, for the specified day of the week
    switch (operation) {
      case 'edit_setpoints':
        _newSchedule.value[dayOfWeek] = {
          ..._newSchedule.value[dayOfWeek],
          [timeOfDay]: scheduleSetting ?? {},
        };
        break;
      case 'edit_time':
        delete _newSchedule.value[dayOfWeek][timeOfDay];
        _newSchedule.value[dayOfWeek] = {
          ..._newSchedule.value[dayOfWeek],
          [newTimeOfDay ?? '']: scheduleSetting ?? {},
        };
        break;
    }

    // Ensure the update is applied to all appropriate days of the week, according to the schedule type
    const updatedDailySchedule = _newSchedule.value[dayOfWeek];
    if (scheduleType === EnumScheduleType.SameEveryDay) {
      // Apply update to Sunday's schedule to all other days of the week
      _newSchedule.value[DayOfWeek.MO] = updatedDailySchedule;
      _newSchedule.value[DayOfWeek.TU] = updatedDailySchedule;
      _newSchedule.value[DayOfWeek.WE] = updatedDailySchedule;
      _newSchedule.value[DayOfWeek.TH] = updatedDailySchedule;
      _newSchedule.value[DayOfWeek.FR] = updatedDailySchedule;
      _newSchedule.value[DayOfWeek.SA] = updatedDailySchedule;
    } else if (scheduleType === EnumScheduleType.WeekdayWeekend) {
      if (dayOfWeek === DayOfWeek.SU) {
        // Apply update to Sunday's schedule to the rest of the weekend
        _newSchedule.value[DayOfWeek.SA] = updatedDailySchedule;
      } else if (dayOfWeek === DayOfWeek.MO) {
        // Apply update to Monday's schedule to the rest of the weekdays
        _newSchedule.value[DayOfWeek.TU] = updatedDailySchedule;
        _newSchedule.value[DayOfWeek.WE] = updatedDailySchedule;
        _newSchedule.value[DayOfWeek.TH] = updatedDailySchedule;
        _newSchedule.value[DayOfWeek.FR] = updatedDailySchedule;
      }
    }

    setScheduleValues(_newSchedule);
  };

  const handleScheduleTypeChange = (newScheduleType: EnumScheduleType) => {
    const _newSchedule = _.cloneDeep(scheduleValues);
    if (newScheduleType === EnumScheduleType.SameEveryDay) {
      // Set all daily schedules to the same value
      const dailySchedule = _.isEqual(scheduleValues.value, EmptyWeeklySchedule)
        ? dailyScheduleDefault
        : scheduleValues.value[DayOfWeek.SU];
      _newSchedule.value[DayOfWeek.SU] = dailySchedule;
      _newSchedule.value[DayOfWeek.MO] = dailySchedule;
      _newSchedule.value[DayOfWeek.TU] = dailySchedule;
      _newSchedule.value[DayOfWeek.WE] = dailySchedule;
      _newSchedule.value[DayOfWeek.TH] = dailySchedule;
      _newSchedule.value[DayOfWeek.FR] = dailySchedule;
      _newSchedule.value[DayOfWeek.SA] = dailySchedule;
    } else if (newScheduleType === EnumScheduleType.WeekdayWeekend) {
      // Set all weekday and weekend schedules to the same values
      const weekendSchedule = _.isEqual(
        scheduleValues.value,
        EmptyWeeklySchedule,
      )
        ? dailyScheduleDefault
        : scheduleValues.value[DayOfWeek.SU];
      const weekdaySchedule = _.isEqual(
        scheduleValues.value,
        EmptyWeeklySchedule,
      )
        ? dailyScheduleDefault
        : scheduleValues.value[DayOfWeek.MO];

      _newSchedule.value[DayOfWeek.MO] = weekdaySchedule;
      _newSchedule.value[DayOfWeek.TU] = weekdaySchedule;
      _newSchedule.value[DayOfWeek.WE] = weekdaySchedule;
      _newSchedule.value[DayOfWeek.TH] = weekdaySchedule;
      _newSchedule.value[DayOfWeek.FR] = weekdaySchedule;
      _newSchedule.value[DayOfWeek.SA] = weekendSchedule;
      _newSchedule.value[DayOfWeek.SU] = weekendSchedule;
    } else if (newScheduleType === EnumScheduleType.Custom) {
      if (_.isEqual(scheduleValues.value, EmptyWeeklySchedule)) {
        _newSchedule.value[DayOfWeek.SU] = dailyScheduleDefault;
        _newSchedule.value[DayOfWeek.MO] = dailyScheduleDefault;
        _newSchedule.value[DayOfWeek.TU] = dailyScheduleDefault;
        _newSchedule.value[DayOfWeek.WE] = dailyScheduleDefault;
        _newSchedule.value[DayOfWeek.TH] = dailyScheduleDefault;
        _newSchedule.value[DayOfWeek.FR] = dailyScheduleDefault;
        _newSchedule.value[DayOfWeek.SA] = dailyScheduleDefault;
      }
    }
    setScheduleValues(_newSchedule);
    setScheduleType(newScheduleType);
  };

  const { user } = useAuthenticator();

  const preferredUnits: TemperatureUnit = user?.preferredUnits ?? Fahrenheit;

  // Get tab labels by schedule type
  // Example: If schedule type is 'SameEveryDay' then only display one tab label 'Every day'
  function getTabLabel(dayOfWeek: DayOfWeek): string {
    const labels = isSmall ? dayOfWeekLabelsMobile : dayOfWeekLabels;
    if (scheduleType === EnumScheduleType.SameEveryDay) {
      return 'Everyday';
    } else {
      return labels[dayOfWeek];
    }
  }

  const handleEdit = (day: DayOfWeek) => {
    return (setting: MyScheduleSetting) => {
      return (oldTime: TimeOfDay, newTime?: TimeOfDay) => {
        if (newTime && parseInt(oldTime) - parseInt(newTime) !== 0) {
          handleScheduleChange('edit_time', day, oldTime, newTime, {
            ...setting,
          });
        } else {
          handleScheduleChange('edit_setpoints', day, oldTime, undefined, {
            ...setting,
          });
        }
      };
    };
  };

  return (
    <>
      <FormControl fullWidth>
        <InputLabel id="schedule-type-label">Schedule Type</InputLabel>
        <Select
          labelId="schedule-type-label"
          id="schedule-type"
          value={scheduleType}
          required
          label="Schedule Type"
          onChange={(e) => {
            handleTypeSelect(e.target.value as EnumScheduleType);
          }}
        >
          <MenuItem value={EnumScheduleType.SameEveryDay}>
            Same Every Day
          </MenuItem>
          <MenuItem value={EnumScheduleType.WeekdayWeekend}>
            Weekday, Weekend
          </MenuItem>
          <MenuItem value={EnumScheduleType.Custom}>Custom</MenuItem>
        </Select>
      </FormControl>
      <br />
      <br />
      <BorderedSection
        title="Thermostat Schedule"
        style={{ marginBottom: '10px' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '10px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <WdTabs
              variant="fullWidth"
              value={selectedDay}
              sx={{ width: '100%' }}
              onChange={handleDaySelect}
            >
              {scheduleValues &&
                daysToDisplay(scheduleType).map((dayOfWeek, index) => {
                  return (
                    <WdTab
                      key={dayOfWeek}
                      value={dayOfWeek}
                      sx={{ minWidth: '45px' }}
                      label={
                        daysToDisplay(scheduleType).length === 2 &&
                        scheduleType === EnumScheduleType.WeekdayWeekend
                          ? label[index]
                          : getTabLabel(dayOfWeek)
                      }
                    />
                  );
                })}
            </WdTabs>
          </div>
        </div>
        <br />
        <List
          sx={{
            width: '100%',
            bgcolor: 'background.paper',
            paddingTop: 0,
          }}
          component="nav"
          aria-labelledby="nested-list-subheader"
          dense
        >
          {dailySchedule &&
            Object.entries(dailySchedule).map(
              ([timeOfDay, scheduleSetting], index) => {
                return (
                  <Card
                    key={index}
                    sx={{ marginBottom: '8px', backgroundColor: '#f2f2f2' }}
                  >
                    <CardHeader
                      title={
                        <Typography
                          sx={{
                            fontSize: '1rem',
                            padding: '0 !',
                          }}
                        >
                          {eventLabels[index]}
                        </Typography>
                      }
                      sx={{
                        boxShadow: '0px 8px 6px -6px rgba(0, 0, 0, 0.35)',
                        padding: '4px 10px!important',
                        borderBottom: '1px solid darkgray',
                        borderRight: 'inherit',
                      }}
                    />
                    <ScheduleItem
                      id={`${index}`}
                      key={selectedDay + '_' + timeOfDay}
                      time={timeOfDay}
                      preferredUnits={preferredUnits}
                      setting={scheduleSetting as MyScheduleSetting}
                      handleEdit={handleEdit(selectedDay)}
                      setpointLimitSettings={{
                        maxHeatSetpointLimit:
                          setpointLimitSettings?.maxHeatSetpointLimit ??
                          defaultAbsoluteLimits['C'].heat.max,
                        maxCoolSetpointLimit:
                          setpointLimitSettings?.maxCoolSetpointLimit ??
                          defaultAbsoluteLimits['C'].cool.max,
                        minHeatSetpointLimit:
                          setpointLimitSettings?.minHeatSetpointLimit ??
                          defaultAbsoluteLimits['C'].heat.min,
                        minCoolSetpointLimit:
                          setpointLimitSettings?.minCoolSetpointLimit ??
                          defaultAbsoluteLimits['C'].cool.min,
                      }}
                    />
                  </Card>
                );
              },
            )}
        </List>
      </BorderedSection>
    </>
  );
}
